/**
 * Les pièces de l'entrée dans le live que l'élève (`useStudentLiveEntryState`) et le masteur
 * (`tutor/PreCall/useTutorPreCallState`) partagent : ce que l'ancien `PreCallScreen` faisait pour
 * tous les deux, écrit une fois.
 *
 *  - `useLiveEntrySessionWait` : la séance absente du compte (lien direct, puis connexion), le compte
 *    relu une fois, et au bout de 6 s on n'attend plus (`PreCallScreen.js:128-144`) ;
 *  - `useLiveEntryTracking` : ce que chaque événement du précall porte (`:152-156`) ;
 *  - `useLiveEntryBrowser` : hors de Chrome sur ordinateur, le choix du navigateur, sa sortie
 *    « Continuer sur ce navigateur » et son bouton (`:87-89`, `:628-649`) ;
 *  - `useLiveEntryDeviceCheck` : le moteur du test (`useDeviceCheckMachine`) branché sur le store —
 *    sa vidéo (`setMuteVideo`), son souvenir (`rememberDeviceCheck`), le périphérique qui suit en
 *    séance (`setPrecallDevices`) —, sa vue traduite (`deviceCheckViewModel`), et l'entrée qui rend la
 *    caméra et le micro avant le live, au natif ;
 *  - `useLiveEntryClock` : l'heure, relue chaque minute (« commence à 18h00 » devient « a commencé »).
 *
 * Ce qui reste à chacun : l'élève a son starting block, son signalement silencieux, son micro coupé
 * avant d'entrer et son test qui part seul ; le masteur, ni l'un ni l'autre.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Platform } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { APP_URL } from "../../../../../../data/constants/apiConstants"
import { apiRequest } from "../../../../../../data/redux/actions/api"
import { setPrecallDevices } from "../../../../../../data/redux/actions/liveMode"
import { rememberDeviceCheck } from "../../../../../../data/redux/actions/precallMemory"
import { setMuteVideo } from "../../../../../../data/redux/actions/videoCall"
import { authService } from "../../../../../../data/services/authService"
import { microphoneListening, type DeviceCheckAudience } from "../../../../../../engine/precall/deviceCheckMachine"
import { useDeviceCheckMachine, type VerifiedDeviceCheck } from "../../../../../../engine/precall/useDeviceCheckMachine"
import { browserFamily } from "../../../../../../engine/precall/verifiedMemory"
import { IS_WEB } from "../../../../../../engine/shared/platform"
import { useInterval } from "../../../../../../engine/shared/useInterval"
import {
    appDomainOf,
    CHROME_DOWNLOAD_URL,
    liveBrowserChoiceFor,
    type LiveBrowserDevice,
} from "../../../../adapters/student/LiveEntry/liveBrowserAdapter"
import { deviceCheckViewModel } from "../../../../view-models/student/LiveEntry/deviceCheckViewModel"
import { openExternalUrl } from "../../../hooks/openExternalUrl"
import { browserUserAgent, openLiveAppStore } from "../../../hooks/openLiveAppStore"
import { LIVE_ENTRY_SOUNDS } from "./liveEntrySounds"
import { useCameraHandoff } from "./useCameraHandoff"

/** Sans séance au bout de ce délai, on n'attend plus (`PreCallScreen.js:141`). */
export const SESSION_WAIT_MS = 6000
/** L'heure de la séance se relit chaque minute. */
const CLOCK_MS = 60_000

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ReduxState = any

/** La séance pas encore lue : le compte relu une fois, puis `true` au bout de `SESSION_WAIT_MS`. */
export function useLiveEntrySessionWait(found: boolean, accountId: unknown): boolean {
    const dispatch = useDispatch()
    const askedUserRef = useRef(false)
    useEffect(() => {
        if (found || askedUserRef.current || accountId == null) return
        askedUserRef.current = true
        dispatch(apiRequest(authService.getUser) as never)
    }, [found, accountId, dispatch])
    const [sessionTimedOut, setSessionTimedOut] = useState(false)
    useEffect(() => {
        if (found) return undefined
        const timer = setTimeout(() => setSessionTimedOut(true), SESSION_WAIT_MS)
        return () => clearTimeout(timer)
    }, [found])
    return sessionTimedOut
}

/** Ce que chaque événement Amplitude du précall porte : `live_session_id`, `platform`, `browser`. */
export function useLiveEntryTracking(liveSessionId: string): Record<string, string> {
    return useMemo(
        () => ({
            live_session_id: liveSessionId,
            platform: IS_WEB ? "web" : Platform.OS,
            browser: IS_WEB && typeof navigator !== "undefined" ? browserFamily(navigator.userAgent) : Platform.OS,
        }),
        [liveSessionId]
    )
}

export type LiveEntryBrowser = { device: LiveBrowserDevice; appDomain: string; onPrimary: () => void }

/** Le choix du navigateur, tant qu'il est à l'écran : `browser` ; `null` sinon. */
export function useLiveEntryBrowser(): { browser: LiveEntryBrowser | null; dismissBrowser: () => void } {
    const staticData = useSelector((state: ReduxState) => state?.data?.staticData)
    const [dismissed, setDismissed] = useState(false)
    const device = useMemo(() => liveBrowserChoiceFor({ isWeb: IS_WEB, userAgent: browserUserAgent() }), [])
    const onPrimary = useCallback(() => {
        if (device === "phone") {
            openLiveAppStore("precall_browser_choice", {
                iosAppUrl: staticData?.ios_app_url,
                androidAppUrl: staticData?.android_app_url,
            })
        } else {
            openExternalUrl(CHROME_DOWNLOAD_URL)
        }
    }, [device, staticData])
    const dismissBrowser = useCallback(() => setDismissed(true), [])
    return {
        browser: device != null && !dismissed ? { device, appDomain: appDomainOf(APP_URL), onPrimary } : null,
        dismissBrowser,
    }
}

/**
 * Le test part seul, une fois, comme une salle d'attente de visio vérifie le matériel en arrivant : dès que
 * l'écran sait quoi servir (`ready` : la séance connue, hors du choix du navigateur). Seulement depuis
 * l'accueil du test (`intro`) : un matériel déjà vérifié sur cet appareil (`remembered`) garde sa carte et
 * « Revérifier », et un test parti n'est pas relancé. L'élève et le masteur (Wajih, 04/11 : le
 * clic sur « Vérifier mon matériel » était une étape de trop, et un test jamais lancé laissait l'image noire).
 */
export function useLiveEntryAutoStart(check: { view: { phase: string }; start: () => void }, ready: boolean): void {
    const startedRef = useRef(false)
    const { start } = check
    const { phase } = check.view
    useEffect(() => {
        if (startedRef.current || !ready || phase !== "intro") return
        startedRef.current = true
        start()
    }, [ready, phase, start])
}

/**
 * Le moteur du test du matériel, branché sur le store, sa vue pour la page, et l'entrée qui rend les
 * appareils : au natif, un objectif et un micro ne servent bien qu'un client, et le live ouvre les siens.
 * `enter(feedShown)` entre tout de suite quand rien n'est tenu ; sinon `releasing` passe à vrai — l'écran
 * démonte l'image (`liveEntryCheckProps`), le moteur ferme l'enregistreur du micro (`releaseMicrophone`) —
 * et `onEntered` vient au bout de `CAMERA_HANDOFF_MS` (`useCameraHandoff`). Ce que tient le micro se lit sur
 * la règle du moteur (`microphoneListening`) ; l'image, l'écran le dit (`feedShown`).
 *
 * Le micro n'est rendu que si l'écran le demande (`releasesMicrophone`) : le masteur, oui ; l'élève, pas
 * encore — son entrée au natif reste celle d'avant (l'image seule) tant qu'elle n'a pas été marchée sur un
 * téléphone et une tablette (issue de suite du précall de l'élève).
 */
export function useLiveEntryDeviceCheck(args: {
    audience: DeviceCheckAudience
    tracking: Record<string, string>
    /** Ouvre le live, une fois les appareils rendus. */
    onEntered: () => void
    /** Le micro aussi est rendu avant d'entrer (pas seulement l'image de la caméra). */
    releasesMicrophone: boolean
    /** La vidéo coupée (`liveMode.isVideoMuted`), que l'écran lit une fois (`useLiveEntryVideoMuted`). */
    isVideoMuted: boolean
}) {
    const dispatch = useDispatch()
    const { isVideoMuted } = args
    const verified = (useSelector((state: ReduxState) => state?.precallMemory?.verified) ??
        null) as VerifiedDeviceCheck | null
    const handoff = useCameraHandoff(args.onEntered)
    const check = useDeviceCheckMachine({
        audience: args.audience,
        role: args.audience,
        isVideoMuted,
        onSetVideoMuted: (muted) => dispatch(setMuteVideo(muted)),
        verified,
        onVerified: (next) => dispatch(rememberDeviceCheck(next)),
        onDevicesChange: (devices) => dispatch(setPrecallDevices(devices)),
        tracking: args.tracking,
        chimeSource: LIVE_ENTRY_SOUNDS.chime,
        releaseMicrophone: args.releasesMicrophone && handoff.releasing,
    })
    // Le niveau du micro redessine l'écran dix fois par seconde ; la vue du test, elle, change rarement.
    const vm = useMemo(
        () => deviceCheckViewModel({ view: check.view, isWeb: IS_WEB, isVideoMuted }),
        [check.view, isVideoMuted]
    )
    const { enter: handOff } = handoff
    const { releasesMicrophone } = args
    const { view } = check
    const enter = useCallback(
        (feedShown: boolean) => handOff(feedShown || (releasesMicrophone && microphoneListening(view, false))),
        [handOff, releasesMicrophone, view]
    )
    return { check, vm, releasing: handoff.releasing, enter }
}

/** La vidéo coupée avant d'entrer (`liveMode.isVideoMuted`) : lue une fois par écran, passée au test. */
export function useLiveEntryVideoMuted(): boolean {
    return useSelector((state: ReduxState) => state?.liveMode?.isVideoMuted === true)
}

/** L'heure, relue chaque minute. */
export function useLiveEntryClock(): number {
    const [now, setNow] = useState(() => Date.now())
    useInterval(() => setNow(Date.now()), CLOCK_MS)
    return now
}
